import { createRef, useEffect, useState } from 'react';
import { View } from 'react-native';

import { FeedbackFilePayload } from './feedbackFile';

export const feedbackPageRef = createRef<View>();
export const feedbackCropRef = createRef<View>();

export const MIN_CAPTURE_EDGE = 80;

export interface CaptureRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type CaptureHandle = 'nw' | 'ne' | 'sw' | 'se' | 'n' | 'e' | 's' | 'w';

export interface FeedbackCaptureRequest {
  onDone: (file: FeedbackFilePayload) => void;
  onDismiss: () => void;
}

let captureOpen = false;
let queuedPath: string | null = null;
let releaseNavigation: ((path: string) => void) | null = null;
let captureRequest: FeedbackCaptureRequest | null = null;
const captureListeners = new Set<() => void>();

function emitCaptureRequest(): void {
  captureListeners.forEach(listener => listener());
}

export function clampCaptureRect(
  rect: CaptureRect,
  frame: CaptureRect,
): CaptureRect {
  const minWidth = Math.min(MIN_CAPTURE_EDGE, frame.width);
  const minHeight = Math.min(MIN_CAPTURE_EDGE, frame.height);
  const width = Math.min(frame.width, Math.max(minWidth, rect.width));
  const height = Math.min(frame.height, Math.max(minHeight, rect.height));
  const x = Math.min(Math.max(rect.x, frame.x), frame.x + frame.width - width);
  const y = Math.min(
    Math.max(rect.y, frame.y),
    frame.y + frame.height - height,
  );
  return { x, y, width, height };
}

/**
 * The Done and Cancel bar sits on top of the page. The selection stops above
 * that bar, including the corner grips, so the buttons stay clickable.
 * The picture itself still uses the full page frame.
 */
export function clipCaptureFrame(
  frame: CaptureRect,
  hostHeight: number,
  reservedBottom: number,
): CaptureRect {
  if (hostHeight <= 0 || reservedBottom <= 0 || frame.height <= 0) {
    return frame;
  }
  const room = hostHeight - reservedBottom - frame.y;
  if (room >= frame.height) {
    return frame;
  }
  if (room < Math.min(MIN_CAPTURE_EDGE, frame.height)) {
    return frame;
  }
  return {
    x: frame.x,
    y: frame.y,
    width: frame.width,
    height: room,
  };
}

export function defaultCaptureRect(frame: CaptureRect): CaptureRect {
  const width = Math.min(
    frame.width,
    Math.max(
      Math.min(MIN_CAPTURE_EDGE, frame.width),
      Math.round(frame.width * 0.7),
    ),
  );
  const height = Math.min(
    frame.height,
    Math.max(
      Math.min(MIN_CAPTURE_EDGE, frame.height),
      Math.round(frame.height * 0.7),
    ),
  );
  return clampCaptureRect(
    {
      x: frame.x + (frame.width - width) / 2,
      y: frame.y + (frame.height - height) / 2,
      width,
      height,
    },
    frame,
  );
}

export function moveCaptureRect(
  origin: CaptureRect,
  dx: number,
  dy: number,
  frame: CaptureRect,
): CaptureRect {
  return clampCaptureRect(
    {
      x: origin.x + dx,
      y: origin.y + dy,
      width: origin.width,
      height: origin.height,
    },
    frame,
  );
}

export function resizeCaptureRect(
  origin: CaptureRect,
  dx: number,
  dy: number,
  handle: CaptureHandle,
  frame: CaptureRect,
): CaptureRect {
  const minWidth = Math.min(MIN_CAPTURE_EDGE, frame.width);
  const minHeight = Math.min(MIN_CAPTURE_EDGE, frame.height);
  const fromWest = handle === 'nw' || handle === 'sw' || handle === 'w';
  const fromEast = handle === 'ne' || handle === 'se' || handle === 'e';
  const fromNorth = handle === 'nw' || handle === 'ne' || handle === 'n';
  const fromSouth = handle === 'sw' || handle === 'se' || handle === 's';
  const east = origin.x + origin.width;
  const south = origin.y + origin.height;
  const frameEast = frame.x + frame.width;
  const frameSouth = frame.y + frame.height;

  const x = fromWest
    ? Math.min(Math.max(origin.x + dx, frame.x), east - minWidth)
    : origin.x;
  const width = fromWest
    ? east - x
    : fromEast
    ? Math.max(Math.min(east + dx, frameEast), origin.x + minWidth) - origin.x
    : origin.width;
  const y = fromNorth
    ? Math.min(Math.max(origin.y + dy, frame.y), south - minHeight)
    : origin.y;
  const height = fromNorth
    ? south - y
    : fromSouth
    ? Math.max(Math.min(south + dy, frameSouth), origin.y + minHeight) -
      origin.y
    : origin.height;

  return clampCaptureRect({ x, y, width, height }, frame);
}

export function beginFeedbackCapture(): void {
  captureOpen = true;
}

export function isFeedbackCaptureOpen(): boolean {
  return captureOpen;
}

export function holdFeedbackNavigation(path: string): boolean {
  if (!captureOpen) {
    return false;
  }
  queuedPath = path;
  return true;
}

export function bindFeedbackCaptureRelease(
  release: (path: string) => void,
): () => void {
  releaseNavigation = release;
  return () => {
    if (releaseNavigation === release) {
      releaseNavigation = null;
    }
  };
}

/** Forget a route that was waiting. Used when the picture is kept so leaving the page cannot drop it. */
export function dropQueuedFeedbackNavigation(): void {
  queuedPath = null;
}

export function endFeedbackCapture(): void {
  captureOpen = false;
  const path = queuedPath;
  queuedPath = null;
  if (path && releaseNavigation) {
    releaseNavigation(path);
  }
}

export function requestFeedbackCapture(next: FeedbackCaptureRequest): void {
  captureRequest = next;
  beginFeedbackCapture();
  emitCaptureRequest();
}

export function dismissFeedbackCapture(): void {
  captureRequest = null;
  emitCaptureRequest();
}

export function useFeedbackCaptureRequest(): FeedbackCaptureRequest | null {
  const [current, setCurrent] = useState(captureRequest);
  useEffect(() => {
    const listener = () => setCurrent(captureRequest);
    captureListeners.add(listener);
    setCurrent(captureRequest);
    return () => {
      captureListeners.delete(listener);
    };
  }, []);
  return current;
}

export function resetFeedbackCaptureState(): void {
  captureOpen = false;
  queuedPath = null;
  captureRequest = null;
  emitCaptureRequest();
}
